/*
  pb.js - shared behaviour for the pb-* sections. Loaded once, from sections/pb-global.liquid.

  <pb-carousel>        [data-pb-track] scroll-snap row of slides, [data-pb-prev] / [data-pb-next]
                       buttons, optional [data-pb-dots]. Adds .is-active to the slide nearest the
                       centre of the track (used by video reels for the "active card" look).
  <pb-video>           wraps one <video>. data-autoplay="true" plays muted while on screen.
                       A click on [data-pb-play] (or the video) toggles play/pause; when
                       data-sound-on-click="true" the first click also unmutes.
  <pb-gallery>         product media: [data-pb-track] of [data-pb-slide] (data-media-id),
                       [data-pb-thumb] buttons (data-index), [data-pb-prev]/[data-pb-next],
                       [data-pb-counter]. Listens for the "pb:variant-change" event
                       (detail.mediaId) and scrolls to that media.
  <pb-sticky-atc>      data-target="<css selector of the main buy area>". Shown once the target
                       has scrolled above the viewport. [data-pb-sticky-submit] with
                       data-form="<form id>" submits that product form via requestSubmit().
  <pb-variant-picker>  radio inputs named "pb-option-<n>", a <script type="application/json"
                       data-pb-variants> holding [{id, options, available, price_html,
                       compare_html, save_html, media_id}], data-form-id, data-section-id.
                       Updates the form's [name=id], [data-pb-price], [data-pb-compare],
                       [data-pb-save], the ATC button and the URL, then dispatches
                       "pb:variant-change" on document.
*/
(function () {
  if (window.__pbLoaded) return;
  window.__pbLoaded = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollToSlide(track, slide) {
    if (!track || !slide) return;
    var left = slide.offsetLeft - track.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
    if (track.dataset.pbAlign === 'start') left = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function nearestIndex(track, slides) {
    var center = track.scrollLeft + track.clientWidth / 2;
    if (track.dataset.pbAlign === 'start') center = track.scrollLeft + 1;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      var point = s.offsetLeft - track.offsetLeft + (track.dataset.pbAlign === 'start' ? 0 : s.clientWidth / 2);
      var d = Math.abs(point - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  /* ---------- carousel ---------- */
  if (!customElements.get('pb-carousel')) {
    customElements.define(
      'pb-carousel',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-pb-track]');
          if (!this.track) return;
          this.slides = Array.prototype.slice.call(this.track.children);
          this.prev = this.querySelector('[data-pb-prev]');
          this.next = this.querySelector('[data-pb-next]');
          this.dots = this.querySelector('[data-pb-dots]');
          var self = this;
          if (this.prev) this.prev.addEventListener('click', function () { self.go(-1); });
          if (this.next) this.next.addEventListener('click', function () { self.go(1); });
          if (this.dots && this.slides.length > 1) {
            this.dots.innerHTML = '';
            this.slides.forEach(function (slide, i) {
              var b = document.createElement('button');
              b.type = 'button';
              b.className = 'pb-dot';
              b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
              b.addEventListener('click', function () { scrollToSlide(self.track, self.slides[i]); });
              self.dots.appendChild(b);
            });
          }
          var ticking = false;
          this.track.addEventListener('scroll', function () {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(function () { self.update(); ticking = false; });
          }, { passive: true });
          window.addEventListener('resize', function () { self.update(); });
          this.update();
          var start = parseInt(this.dataset.startIndex || '0', 10);
          if (start > 0 && this.slides[start]) {
            this.track.scrollLeft = this.slides[start].offsetLeft - this.track.offsetLeft - (this.track.clientWidth - this.slides[start].clientWidth) / 2;
          }
        }
        go(dir) {
          if (!this.slides.length) return;
          var step = Math.max(1, Math.floor(this.track.clientWidth / (this.slides[0].clientWidth || 1)));
          if (this.track.dataset.pbAlign !== 'start') step = 1;
          var i = Math.min(this.slides.length - 1, Math.max(0, this.index + dir * step));
          scrollToSlide(this.track, this.slides[i]);
        }
        update() {
          if (!this.slides || !this.slides.length) return;
          this.index = nearestIndex(this.track, this.slides);
          var self = this;
          this.slides.forEach(function (s, i) { s.classList.toggle('is-active', i === self.index); });
          if (this.dots) {
            Array.prototype.forEach.call(this.dots.children, function (d, i) {
              d.setAttribute('aria-current', i === self.index ? 'true' : 'false');
            });
          }
          var maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll;
          this.classList.toggle('pb-carousel--static', maxScroll <= 0);
        }
      }
    );
  }

  /* ---------- video ---------- */
  if (!customElements.get('pb-video')) {
    customElements.define(
      'pb-video',
      class extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          if (!this.video) return;
          var self = this;
          this.video.setAttribute('playsinline', '');
          var toggle = function (e) {
            if (e) e.preventDefault();
            if (self.dataset.soundOnClick === 'true' && self.video.muted) {
              self.video.muted = false;
              self.classList.add('is-unmuted');
              if (!self.video.paused) return;
            }
            if (self.video.paused) self.play(); else self.pause();
          };
          Array.prototype.forEach.call(this.querySelectorAll('[data-pb-play]'), function (b) {
            b.addEventListener('click', toggle);
          });
          if (this.dataset.clickVideo !== 'false') this.video.addEventListener('click', toggle);
          this.video.addEventListener('play', function () { self.classList.add('is-playing'); });
          this.video.addEventListener('pause', function () { self.classList.remove('is-playing'); });
          if (this.dataset.autoplay === 'true' && !reduceMotion && 'IntersectionObserver' in window) {
            this.video.muted = true;
            this.observer = new IntersectionObserver(function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) self.play(); else if (!self.video.paused) self.video.pause();
              });
            }, { threshold: 0.35 });
            this.observer.observe(this);
          }
        }
        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }
        play() {
          var p = this.video.play();
          if (p && p.catch) p.catch(function () {});
        }
        pause() {
          this.video.pause();
        }
      }
    );
  }

  /* ---------- product gallery ---------- */
  if (!customElements.get('pb-gallery')) {
    customElements.define(
      'pb-gallery',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-pb-track]');
          if (!this.track) return;
          this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-pb-slide]'));
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pb-thumb]'));
          this.counter = this.querySelector('[data-pb-counter]');
          this.index = 0;
          var self = this;
          this.thumbs.forEach(function (t) {
            t.addEventListener('click', function () { self.goTo(parseInt(t.dataset.index, 10) || 0); });
          });
          var prev = this.querySelector('[data-pb-prev]');
          var next = this.querySelector('[data-pb-next]');
          if (prev) prev.addEventListener('click', function () { self.goTo(self.index - 1); });
          if (next) next.addEventListener('click', function () { self.goTo(self.index + 1); });
          var ticking = false;
          this.track.addEventListener('scroll', function () {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(function () { self.sync(); ticking = false; });
          }, { passive: true });
          this.onVariant = function (e) {
            if (!e.detail || !e.detail.mediaId) return;
            for (var i = 0; i < self.slides.length; i++) {
              if (String(self.slides[i].dataset.mediaId) === String(e.detail.mediaId)) {
                self.goTo(i);
                break;
              }
            }
          };
          document.addEventListener('pb:variant-change', this.onVariant);
          this.sync();
        }
        disconnectedCallback() {
          document.removeEventListener('pb:variant-change', this.onVariant);
        }
        goTo(i) {
          if (!this.slides.length) return;
          if (i < 0) i = this.slides.length - 1;
          if (i >= this.slides.length) i = 0;
          this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
          this.setActive(i);
        }
        sync() {
          if (!this.slides.length) return;
          var w = this.track.clientWidth || 1;
          var i = Math.round(this.track.scrollLeft / w);
          this.setActive(Math.min(this.slides.length - 1, Math.max(0, i)));
        }
        setActive(i) {
          if (i === this.index && this._init) return;
          this._init = true;
          this.index = i;
          var self = this;
          this.thumbs.forEach(function (t) {
            var on = parseInt(t.dataset.index, 10) === i;
            t.setAttribute('aria-current', on ? 'true' : 'false');
            if (on && t.scrollIntoView && t.parentElement && t.parentElement.scrollWidth > t.parentElement.clientWidth + 2) {
              t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
            }
          });
          this.slides.forEach(function (s, n) {
            s.classList.toggle('is-active', n === i);
            if (n !== i) {
              Array.prototype.forEach.call(s.querySelectorAll('video'), function (v) { if (!v.paused) v.pause(); });
            }
          });
          if (this.counter) this.counter.textContent = i + 1 + ' / ' + this.slides.length;
          this.dispatchEvent(new CustomEvent('pb:gallery-change', { bubbles: true, detail: { index: i } }));
          void self;
        }
      }
    );
  }

  /* ---------- sticky add to cart ---------- */
  if (!customElements.get('pb-sticky-atc')) {
    customElements.define(
      'pb-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          var target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
          var self = this;
          this.setAttribute('aria-hidden', 'true');
          Array.prototype.forEach.call(this.querySelectorAll('[data-pb-sticky-submit]'), function (btn) {
            btn.addEventListener('click', function (e) {
              var form = document.getElementById(btn.dataset.form || '');
              if (form && typeof form.requestSubmit === 'function') {
                e.preventDefault();
                form.requestSubmit();
              } else if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
              }
            });
          });
          if (!target || !('IntersectionObserver' in window)) return;
          this.observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              self.classList.toggle('is-visible', passed);
              self.setAttribute('aria-hidden', passed ? 'false' : 'true');
              document.body.classList.toggle('pb-sticky-atc-open', passed);
            });
          }, { threshold: 0 });
          this.observer.observe(target);
        }
        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          document.body.classList.remove('pb-sticky-atc-open');
        }
      }
    );
  }

  /* ---------- variant picker (only used when a product has real variants) ---------- */
  if (!customElements.get('pb-variant-picker')) {
    customElements.define(
      'pb-variant-picker',
      class extends HTMLElement {
        connectedCallback() {
          var json = this.querySelector('[data-pb-variants]');
          if (!json) return;
          try { this.variants = JSON.parse(json.textContent); } catch (e) { this.variants = []; }
          var self = this;
          this.addEventListener('change', function () { self.onChange(); });
        }
        selected() {
          var values = [];
          var groups = this.querySelectorAll('[data-pb-option]');
          Array.prototype.forEach.call(groups, function (g) {
            var checked = g.querySelector('input:checked');
            values.push(checked ? checked.value : null);
          });
          return values;
        }
        /* bundle cards: each card shows the price of the variant you would get if you picked it,
           with the other options left as they are. */
        updateAltCards(values) {
          var self = this;
          Array.prototype.forEach.call(this.querySelectorAll('[data-pb-alt-value]'), function (card) {
            var idx = parseInt(card.dataset.pbAltIndex, 10);
            if (isNaN(idx)) return;
            var wanted = values.slice();
            wanted[idx] = card.dataset.pbAltValue;
            var match = null;
            for (var i = 0; i < self.variants.length; i++) {
              var v = self.variants[i];
              var ok = true;
              for (var j = 0; j < wanted.length; j++) if (wanted[j] !== null && v.options[j] !== wanted[j]) ok = false;
              if (ok) { match = v; break; }
            }
            var set = function (sel, html) {
              var el = card.querySelector(sel);
              if (!el) return;
              el.innerHTML = html || '';
              el.hidden = !html;
            };
            set('[data-pb-alt-price]', match ? match.price_html : '');
            set('[data-pb-alt-compare]', match ? match.compare_html : '');
            set('[data-pb-alt-save]', match ? match.save_html : '');
            card.classList.toggle('is-unavailable', !(match && match.available));
          });
        }
        onChange() {
          var values = this.selected();
          var variant = null;
          for (var i = 0; i < this.variants.length; i++) {
            var v = this.variants[i];
            var match = true;
            for (var j = 0; j < values.length; j++) if (v.options[j] !== values[j]) match = false;
            if (match) { variant = v; break; }
          }
          Array.prototype.forEach.call(this.querySelectorAll('[data-pb-option]'), function (g, idx) {
            var label = g.querySelector('[data-pb-option-value]');
            if (label) label.textContent = values[idx] || '';
          });
          var sectionId = this.dataset.sectionId;
          var form = document.getElementById(this.dataset.formId || '');
          var idInput = form ? form.querySelector('[name="id"]') : null;
          var button = document.getElementById('ProductSubmitButton-' + sectionId);
          var scope = document.getElementById('PbProduct-' + sectionId) || document;
          var set = function (sel, html) {
            Array.prototype.forEach.call(scope.querySelectorAll(sel), function (el) {
              el.innerHTML = html || '';
              el.hidden = !html;
            });
          };
          if (!variant) {
            if (button) {
              button.disabled = true;
              var s0 = button.querySelector('span');
              if (s0) s0.textContent = this.dataset.unavailableText || 'Unavailable';
            }
            return;
          }
          if (idInput) {
            idInput.value = variant.id;
            idInput.disabled = false;
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          set('[data-pb-price]', variant.price_html);
          set('[data-pb-compare]', variant.compare_html);
          set('[data-pb-save]', variant.save_html);
          if (button) {
            button.disabled = !variant.available;
            var span = button.querySelector('span');
            if (span) span.textContent = variant.available ? this.dataset.addText || 'Add to cart' : this.dataset.soldOutText || 'Sold out';
          }
          Array.prototype.forEach.call(document.querySelectorAll('[data-pb-sticky-variant="' + sectionId + '"]'), function (el) {
            el.textContent = variant.title || '';
          });
          if (window.history && window.history.replaceState && this.dataset.updateUrl !== 'false') {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url.toString());
          }
          this.updateAltCards(values);
          document.dispatchEvent(new CustomEvent('pb:variant-change', { detail: { variantId: variant.id, mediaId: variant.media_id, sectionId: sectionId } }));
        }
      }
    );
  }
})();
